import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import Transcript from "./transcript/videojs-transcript";

import "./index.scss";

export function VideoPlayer(props) {
  const intl = useIntl();
  const [loaded, setLoaded] = useState(false);
  const videoNode = useRef(null);
  const transcriptArea = useRef(null);
  const [player, setPlayer] = useState(null);
  const [viewTranscript, setViewTranscript] = useState(false);

  const handlePlayerReady = function () {
    if (player) {
      getTranscript();
      setTranscriptBtns(); // ensure button appears on ready
    }
  };

  useEffect(() => {
    const instance = videojs(
      videoNode.current,
      {
        sources: [{ src: props.data.videoURL, type: "application/x-mpegURL" }],
        controlBar: {
          playToggle: true,
          captionsButton: true,
          chaptersButton: false,
          subtitlesButton: false,
          currentTime: true,
          currentTimeDisplay: true,
          liveCurrentTime: true,
          durationDisplay: true,
          progressControl: { seekBar: true },
          fullscreenToggle: true,
          volumePanel: { inline: true },
          playbackRateMenuButton: true,
          pictureInPictureToggle: false,
        },
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
        castButton: false,
        textTrackSettings: true,
      },
      handlePlayerReady
    );

    setPlayer(instance);
    Transcript(window, videojs);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (videoNode.current && player) {
      if (!player.paused()) videoNode.current.pause();
      cleanupTracks();
      player.src([{ src: props.data.videoURL, type: "application/x-mpegURL" }]);
      videoNode.current.load();
      getTranscript();
    }
  }, [props.data.videoURL]);

  useEffect(() => {
    if (player) {
      getTranscript();
      setTranscriptBtns(); // initial button render on dependency change
      window.addEventListener("resize", setTranscriptBtns);
    }
    return () => window.removeEventListener("resize", setTranscriptBtns);
  }, [loaded, player]);

  const setTranscriptBtns = () => {
    if (!props.data.srt || props.data.srt === "") {
      return;
    }

    const myButton = player.controlBar.addChild("button");
    myButton.addClass("transcript-btn");
    const myButtonDom = myButton.el();
    myButtonDom.innerHTML = `<span style="cursor: pointer; font-size: 14px">${intl.formatMessage(
      { id: "Transcript", defaultMessage: "Transcript" }
    )}</span>`;
    myButtonDom.style.display = window.innerWidth <= 720 ? "none" : "block";
    const myButton2 = player.controlBar.addChild("button");
    myButton2.addClass("transcript-btn");
    const myButtonDom2 = myButton2.el();
    myButtonDom2.innerHTML = `<span style="cursor: pointer; font-size: 14px; border-bottom: 1px solid white">${intl.formatMessage(
      { id: "Transcript", defaultMessage: "Transcript" }
    )}</span>`;
    myButtonDom2.style.display = window.innerWidth <= 720 ? "none" : "none";

    myButtonDom.onclick = () => {
      setViewTranscript(true);
      myButtonDom.style.display = "none";
      myButtonDom2.style.display = "block";
    };
    myButtonDom2.onclick = () => {
      setViewTranscript(false);
      myButtonDom2.style.display = "none";
      myButtonDom.style.display = "block";
    };
  };

  const getTranscript = async () => {
    if (!props.data.srt || props.data.srt === "") {
      return;
    }

    const baseUrl =
      window.CONTENT_BASE_URL ||
      window.location.origin + window.location.pathname;

    try {
      let url = props.preview
        ? props.data.srtURL
        : `${baseUrl}locales/${intl.locale}/srt/${props.data.srt}`;
      let response = await fetch(url);
      let data = await response.text();
      if (data.includes("!DOCTYPE") || data.includes("<Error>")) {
        url = `${baseUrl}locales/en/srt/${props.data.srt}`;
        response = await fetch(url);
        data = await response.text();
      }
      setTranscript(data);
    } catch (error) {
      console.error("Error fetching transcript:", error);
    }
  };

  /**
   * Remove all existing caption tracks from the video element and Video.js player.
   */
  const cleanupTracks = () => {
    videoNode.current.querySelectorAll("track").forEach((t) => t.remove());
    Array.from(player.remoteTextTracks()).forEach((t) =>
      player.removeRemoteTextTrack(t)
    );
  };

  /**
   * Inject a native <track> element for Safari to parse blob-based captions.
   * @param {string} vttUrl URL to the generated WebVTT blob.
   */
  const loadSafariCaption = (vttUrl) => {
    const trackEl = document.createElement("track");
    trackEl.kind = "captions";
    trackEl.srclang = intl.locale;
    trackEl.label = intl.locale === "en" ? "English" : intl.locale;
    trackEl.default = true;
    trackEl.src = vttUrl;
    videoNode.current.appendChild(trackEl);

    const [txt] = videoNode.current.textTracks;
    txt.mode = "hidden";
    txt.mode = "showing";
  };

  /**
   * Add a remote text track via Video.js API for non-Safari browsers.
   * @param {string} vttUrl URL to the generated WebVTT blob.
   */
  const loadRemoteCaption = (vttUrl) => {
    const remote = player.addRemoteTextTrack(
      {
        kind: "captions",
        srclang: intl.locale,
        label: intl.locale === "en" ? "English" : intl.locale,
        src: vttUrl,
        default: true,
        manualCleanup: true,
      },
      false
    );
    const txt = remote.track;
    txt.mode = "hidden";
    txt.mode = "showing";
  };

  /**
   * Initialize the transcript plugin once captions have loaded.
   */
  const initializeTranscript = () => {
    setTimeout(() => {
      const transcript = player.transcript({
        showTitle: false,
        showTrackSelector: false,
      });
      const node = transcriptArea.current;
      node.innerHTML = "";
      node.appendChild(transcript.el());
    }, 0);
  };

  /**
   * Convert raw SRT data to WebVTT, load captions, and render transcript.
   * @param {string} data Raw SRT text.
   */
  const setTranscript = (data) => {
    const vttBlob = new Blob([srt2webvtt(data)], {
      type: "text/vtt;charset=utf-8",
    });
    const vttUrl = URL.createObjectURL(vttBlob);

    cleanupTracks();
    if (videojs.browser.IS_SAFARI) {
      loadSafariCaption(vttUrl);
    } else {
      loadRemoteCaption(vttUrl);
    }
    initializeTranscript();
  };

  /**
   * Convert SRT formatted text into a WebVTT string.
   */
  const srt2webvtt = (data) => {
    let srt = data.replace(/\r+/g, "");
    srt = srt.trim();
    const cuelist = srt.split("\n\n");
    let result = "WEBVTT\n\n";
    cuelist.forEach((cue) => {
      result += convertSrtCue(cue);
    });
    return result;
  };

  /**
   * Convert a single SRT cue block into WebVTT format.
   */
  const convertSrtCue = (caption) => {
    const lines = caption.split(/\n/);
    while (lines.length > 3) {
      lines[2] += "\n" + lines.splice(3, 1);
    }
    let idx = 0;
    let cueText = "";
    if (!/\d+:\d+:\d+/.test(lines[0]) && /\d+:\d+:\d+/.test(lines[1])) {
      cueText += lines[0] + "\n";
      idx = 1;
    }
    const timeMatch = lines[idx].match(
      /(\d+):(\d+):(\d+)(?:,(\d+))?\s*-->\s*(\d+):(\d+):(\d+)(?:,(\d+))?/
    );
    if (!timeMatch) return "";
    cueText +=
      `${timeMatch[1]}:${timeMatch[2]}:${timeMatch[3]}.${timeMatch[4]} --> ` +
      `${timeMatch[5]}:${timeMatch[6]}:${timeMatch[7]}.${timeMatch[8]}\n`;
    cueText += lines[idx + 1] + "\n\n";
    return cueText;
  };

  return (
    <div className="basicVideo" id="basicVideo">
      <div className="video-container">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoNode}
          crossOrigin="anonymous"
          className="video-js custom-videojs-components vjs-big-play-centered vjs-fluid"
          id="my-player"
          controls
          preload="true"
        >
          <p className="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading
            to a web browser that
            <a href="https://videojs.com/html5-video-support/" target="_blank">
              supports HTML5 video
            </a>
          </p>
        </video>
        <div
          className="custom-video-js-transcript"
          ref={transcriptArea}
          style={{ display: !viewTranscript ? "none" : undefined }}
        />
      </div>
    </div>
  );
}

VideoPlayer.propTypes = {};
VideoPlayer.defaultProps = {};
